import React, { useEffect, useMemo, useRef, useState } from "react";
import "./reservation.css";

import RfmHeaderMain from "./components/RfmHeaderMain";
import RfmSidebar from "./components/RfmSidebar";
import RfmFloor from "./components/RfmFloor";
import RfmTableOffcanvas from "./components/RfmTableOffcanvas";
import RfmTableRequestsPanel from "./components/RfmTableRequestsPanel";
//ALERTS 
import ErrorAlert from "../Alerts/ErrorAlert";
import AlertSmall from "../Alerts/AlertSmall";
//API
import { BranchOfficeEnvironmentsFetchAPI } from "../../api/BranchOfficeEnvironments";
import { TablesFetchAPI } from "../../api/Tables";
import { ReservationFetchAPI } from "../../api/reservation";
import { TableSessionsFetchAPI } from "../../api/TableSessions";
import validSession from "../utils/validSession";
import Socket from "../utils/Socket.config";
import Swal from "sweetalert2";
import useAlarmSound from "../../hooks/useAlarmSound";
import { ensureGeneralData } from "../utils/ensureGeneralData";

// MODALS
import RfmCreateZoneModal from "./components/modals/RfmCreateZoneModal";
import RfmCreateTableModal from "./components/modals/RfmCreateTableModal";
import RfmReservationModal from "./components/modals/RfmReservationModal";
import RfmMergeTablesModal from "./components/modals/RfmMergeTablesModal";
import RfmSplitBillModal from "./components/modals/RfmSplitBillModal";
import RfmManageStatusModal from "./components/modals/RfmManageStatusModal";
import RfmCreateObjectModal from "./components/modals/RfmCreateObjectModal";
import RfmEditZoneModal from "./components/modals/RfmEditZoneModal";
import RfmDeleteZoneModal from "./components/modals/RfmDeleteZoneModal";
import RfmAddComensalModal from "./components/modals/RfmAddComensalModal";
import RfmAddOrderToComensalModal from "./components/modals/RfmAddOrderToComensalModal";
import RfmCloseTableSessionModal from "./components/modals/RfmCloseTableSessionModal";
import RfmMoveTableSessionModal from "./components/modals/RfmMoveTableSessionModal";
import RfmModals from "./components/modals/RfmModals";
import ModalLoginUserByCode from "../PaymentPost/ModalLoginUserByCode";
import TableDiscountRequestsModal from "./components/modals/TableDiscountRequestsModal";
import PaymentPost from "../PaymentPost/PaymentPost";
import MesasPage from "./MesasPage";
import DailySummary from "./components/new/DailySummary/DailySummary";
import ZoneTabs from "./components/new/ZoneTabs/ZoneTabs";
import './css/styles.css'

const rectsOverlap = (a, b) =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

const canPlaceRect = ({ x, y, w, h }, tables, objects, zoneId) => {
  // colisión con objetos
  for (const o of objects.filter((o) => o.zone === zoneId)) {
    const r = { x: o.gridX, y: o.gridY, w: o.gridW || 1, h: o.gridH || 1 };
    if (rectsOverlap({ x, y, w, h }, r)) return false;
  }
  // colisión con mesas
  for (const t of tables.filter((t) => t.zone === zoneId)) {
    const r = { x: t.gridX, y: t.gridY, w: t.gridW || 1, h: t.gridH || 1 };
    if (rectsOverlap({ x, y, w, h }, r)) return false;
  }
  return true;
};

const findFirstFreeSpot = (zone, tables, objects, w = 1, h = 1) => {
  if (!zone) return null;
  for (let y = 0; y <= zone.rows - h; y++) {
    for (let x = 0; x <= zone.cols - w; x++) {
      const ok = canPlaceRect({ x, y, w, h }, tables, objects, zone.id);
      if (ok) return { x, y };
    }
  }
  return null;
};

//Helper to get unassigned items from an order (items that are not assigned to any diner)
const getUnassignedItemsFromOrder = (order) => {
  if (!order) return [];

  const groupItems = (order.items || []).map((item, index) => ({
    source: "group",
    index,
    name: item.name,
    qty: Number(item.qty || 1),
    price: Number(item.price || 0),
    diner_id: item.diner_id || null,
  }));

  return groupItems.filter((item) => !item.diner_id);
};


export default function Reservation() {

  // ===== General Data / State =====
  let branchoffice = JSON.parse(localStorage.getItem("branchoffice"));
  let business = JSON.parse(localStorage.getItem("business"));
  let token = localStorage.getItem("x-access-machine-token");
  // ===== STATE PRINCIPAL =====
  const [currentZone, setCurrentZone] = useState("all");
  const [zoomLevel, setZoomLevel] = useState(1);
  const [editMode, setEditMode] = useState(false);

  const [selectedTableId, setSelectedTableId] = useState(null);
  const [selectedObjectId, setSelectedObjectId] = useState(null);

  const [isTableOffcanvasOpen, setIsTableOffcanvasOpen] = useState(false);


  // ====== Login modal ======
  const [showLoginByCode, setShowLoginByCode] = useState(false);
  const notifiedSessionRequestsRef = useRef(new Set());


  /*!---------- Overlays ---------- */
  // const [selectedId, setSelectedId] = useState(null);   // mesa abierta en el panel
  // const [panelMode, setPanelMode] = useState('dock');   // 'dock' | 'modal'
  const [drawerOpen, setDrawerOpen] = useState(false);  // menú de navegación
  /* ---------- resumen ---------- */
  const [summaryCollapsed, setSummaryCollapsed] = useState(true); // colapsado por defecto









  // ====== Alerta sonora de pedidos/solicitudes de mesa ======
  const { startAlarm, stopAlarm, showAudioUnlock, handleUnlockAudio } = useAlarmSound();
  // IDs de mesa con actividad sin revisar (nueva ronda / solicitud de pago) - pinta un punto rojo
  // en esa mesa puntual dentro de la grilla/lista hasta que se abre su panel de detalle.
  const [alertedTableIds, setAlertedTableIds] = useState(() => new Set());
  const markTableAlerted = (tableId) => {
    if (!tableId) return;
    setAlertedTableIds((prev) => {
      if (prev.has(tableId)) return prev;
      const next = new Set(prev);
      next.add(tableId);
      return next;
    });
  };
  const clearTableAlert = (tableId) => {
    setAlertedTableIds((prev) => {
      if (!prev.has(tableId)) return prev;
      const next = new Set(prev);
      next.delete(tableId);
      return next;
    });
  };

  // ====== Fase 10: rendimiento de eventos de sesion de mesa ======
  // Evita peticiones duplicadas en paralelo para la misma mesa/sesion (comparte la misma promesa).
  const inFlightRequestsRef = useRef(new Map());
  // Ultima firma de orden aplicada por mesa ("<order_id>:<shared_order_version>"), para no re-aplicar
  // un snapshot ya procesado ni retroceder ante una version vieja que llegue fuera de orden.
  const orderSignatureByTableRef = useRef(new Map());
  // Ultimo line_status conocido por linea (line_uuid), por mesa - usado solo para detectar el
  // paso DRAFT -> SUBMITTED y disparar el aviso no bloqueante de "nueva ronda" (Fase 6).
  const lineStatusByTableRef = useRef(new Map());
  // Agrupa eventos rapidos por mesa en una sola resolucion (Fase 10).
  const refreshDebounceRef = useRef(new Map());
  // Espejo de `tables` accesible desde closures que no deben re-crearse en cada render (el
  // useEffect del socket solo depende de business/branchoffice).
  const tablesRef = useRef([]);

  // ===== MODALES (GLOBAL) =====
  // "create-zone" | "create-table" | "reservation" | "merge-tables" | "split-bill"
  // "manage-status" | "create-object" | "add-comensal" | "add-order-comensal" | "products-modal"
  const [activeModal, setActiveModal] = useState(null);
  const openModal = (name) => setActiveModal(name);
  const closeModal = () => setActiveModal(null);


  // ===== DATA =====
  const [zones, setZones] = useState([]);

  const [statuses, setStatuses] = useState([
    { id: "available", name: "Disponible", color: "#10b981", icon: "bi bi-check-circle" },
    { id: "occupied", name: "Ocupada", color: "#f59e0b", icon: "bi bi-person-fill" },
    { id: "reserved", name: "Reservada", color: "#8b5cf6", icon: "bi bi-calendar-check" },
  ]);

  // 👇 IMPORTANTE: aquí cada mesa ya trae comensales: []
  const [tables, setTables] = useState([]);
  const [tableSessionRequests, setTableSessionRequests] = useState([]);
  const [tableSessionsByTable, setTableSessionsByTable] = useState({});
  // Historial de actividad del cliente e-commerce, por table_id. El backend lo guarda mientras la
  // sesion esta abierta (getActiveByTable lo trae en `events`, y cada evento nuevo llega por el
  // socket "tableSessionEventRecorded") y lo borra al cerrarse - ver removeTableSession.
  // Vive aparte de tableSessionsByTable a proposito: los payloads de socket no traen `events` y
  // upsertTableSession reemplaza la sesion completa, asi que ahi se perderia.
  const [tableEventsByTable, setTableEventsByTable] = useState({});
  // Ultimo evento que el mesero ya vio por mesa (null = mesa sin eventos al cargar). Lo que llegue
  // despues de eso y no sea este id enciende el punto rojo de "Actividad".
  const [seenEventIdByTable, setSeenEventIdByTable] = useState({});
  // "Pagar con POS": pending physical-charge requests raised from the ecommerce prepay table client,
  // keyed by table_id - see tableSessionPhysicalPaymentRequested below and
  // RfmTableOffcanvas.jsx's "Sesion e-commerce" section, which renders the "Cobrar en terminal"
  // button from this.
  // Cada mesa guarda una LISTA de solicitudes (una mesa con dos QR = dos pedidos que pueden pedir pagar en
  // el POS a la vez): antes era una sola por mesa y la segunda pisaba a la primera, que se perdia.
  const [posPaymentRequestsByTable, setPosPaymentRequestsByTable] = useState({});
  // Sin opciones limpia TODAS las de la mesa (mesa liberada/movida). Con attemptId solo ese cobro (ya se
  // cobro o se libero); con sessionUuid solo las de esa sesion (se cerro) - las del otro pedido se quedan.
  const clearPosPaymentRequest = (tableId, { sessionUuid = null, attemptId = null } = {}) => {
    if (!tableId) return;
    const key = String(tableId);
    setPosPaymentRequestsByTable((prev) => {
      const list = prev[key];
      if (!list || list.length === 0) return prev;
      const remaining = (attemptId || sessionUuid)
        ? list.filter((request) => !(attemptId ? request.payment_attempt_id === attemptId : request.session_uuid === sessionUuid))
        : [];
      if (remaining.length === list.length) return prev;
      const next = { ...prev };
      if (remaining.length > 0) next[key] = remaining; else delete next[key];
      return next;
    });
  };
  // Agrega o reemplaza (por payment_attempt_id) una solicitud en la lista de la mesa.
  const upsertPosPaymentRequest = (tableId, request) => {
    if (!tableId || !request?.payment_attempt_id) return;
    const key = String(tableId);
    setPosPaymentRequestsByTable((prev) => {
      const list = prev[key] || [];
      const without = list.filter((item) => item.payment_attempt_id !== request.payment_attempt_id);
      return { ...prev, [key]: [...without, request] };
    });
  };

  // Card charging (BAC/Banesco/Evertech/KinPOS via ModalPayMet) only lives in the caja screen
  // (PaymentPost.jsx). En vez de navegar a /payments, "Cobrar en terminal" monta esa pantalla
  // embebida y oculta aqui mismo (ver <PaymentPost embedded /> al final del render): solo se ve el
  // Centro de Pagos y el cajero se queda en /reservation. Se desmonta al confirmar, cancelar,
  // vencer la solicitud o cerrar el Centro de Pagos.
  const [posCharge, setPosCharge] = useState(null);
  // Mesa cuyo modal de solicitudes de descuento esta abierto (el cliente e-commerce pide descuento sobre un
  // producto y aqui el personal lo aprueba/rechaza). Se abre desde el aviso, la card o el historial.
  const [discountReviewTableId, setDiscountReviewTableId] = useState(null);
  const handleChargePosPayment = (table, request = null) => {
    // Con dos pedidos en la mesa hay varias solicitudes: la pantalla indica cual cobrar; sin indicarla se
    // cobra la primera (mismo comportamiento de siempre con una sola).
    const target = request || table?.posPaymentRequests?.[0] || table?.posPaymentRequest;
    if (!target) return;
    setPosCharge({
      ...target,
      table_id: table.id,
      table_name: table.name || table.number || table.id
    });
  };
  const handlePosChargeSettled = () => {
    const tableId = posCharge?.table_id;
    // Pequeño margen: ModalPayMet todavia termina de cerrar sus loaders despues de avisar.
    window.setTimeout(() => setPosCharge(null), 500);
    if (!tableId) return;
    // Solo el cobro que se acaba de resolver: la solicitud del otro pedido de la mesa sigue pendiente.
    clearPosPaymentRequest(tableId, { attemptId: posCharge?.payment_attempt_id || null });
    scheduleTableRefresh(tableId, { refreshSession: true, refreshOrders: true });
  };
  const [showTableRequestsPanel, setShowTableRequestsPanel] = useState(false);
  const [processingSessionId, setProcessingSessionId] = useState(null);

  const [objects, setObjects] = useState([
    { id: 1, type: "plant", zone: 1, gridX: 0, gridY: 0, gridW: 1, gridH: 1, label: "" },
  ]);

  // ===== DERIVADOS =====
  const activeZone = useMemo(() => zones.find((z) => z.id === currentZone) || null, [zones, currentZone]);

  const selectedTable = useMemo(
    () => (selectedTableId ? tables.find((t) => t.id === selectedTableId) : null),
    [tables, selectedTableId]
  );

  const remainingForSplit = useMemo(() => {
    if (!selectedTable) return 0;
    const total = Number(selectedTable.total) || 0;
    const paid = Number(selectedTable.paid) || 0;
    return Math.max(0, total - paid);
  }, [selectedTable]);

  const getSessionUuid = (session = {}) => (
    session.session_uuid || session.uuid || session.id || session._id || session.table_session_uuid
  );

  const normalizeTableSession = (raw = {}) => {
    const session = raw.session || raw.table_session || raw;
    const businessData = session.business || raw.business || {};
    const branchofficeData = session.branchoffice || raw.branchoffice || {};
    const table = session.table || raw.table || {};
    const environment = session.environment || session.zone || table.environment || {};
    return {
      session_uuid: getSessionUuid(session),
      table_id: session.table_id || table.id || raw.table_id,
      table_code: session.table_code || table.code || raw.table_code,
      table_name: session.table_name || table.name || raw.table_name,
      branchoffice_environment_id: session.branchoffice_environment_id || table.branchoffice_environment_id || table.environment_id || environment.id,
      zone_id: table.environment_id || environment.id,
      zone_name: table.environment_name || environment.name || session.zone_name || raw.zone_name,
      business_id: session.business_id || raw.business_id || businessData.id,
      business_code: session.business_code || raw.business_code || businessData.code,
      branchoffice_id: session.branchoffice_id || raw.branchoffice_id || branchofficeData.id,
      branchoffice_code: session.branchoffice_code || raw.branchoffice_code || branchofficeData.code,
      status: session.status || raw.status,
      source: session.source || raw.source,
      requested_at: session.requested_at || raw.requested_at || session.createdAt || raw.createdAt,
      approved_at: session.approved_at || raw.approved_at,
      closed_at: session.closed_at || raw.closed_at,
      temp_order_id: session.temp_order_id || session.active_temp_order_id || session.order_id || raw.temp_order_id || raw.active_temp_order_id || raw.order_id,
      temp_order_code: session.temp_order_code || session.active_temp_order_code || session.order_code || raw.temp_order_code || raw.active_temp_order_code || raw.order_code,
      has_active_order: Boolean(session.has_active_order || raw.has_active_order || session.active_temp_order_id || raw.active_temp_order_id || session.temp_order_id || raw.temp_order_id),
      client_name: session.client_name || session.customer_name || session.customer?.name || session.client?.name || raw.client_name,
      // Fase 3/13: campos aditivos confirmados en controller/tableSessions.js#getActiveByTable y
      // services/tableSessions.js#getPendingSessions del backend real - no rompen nada si faltan
      // (sesiones legacy o payloads de eventos que no los incluyen quedan en 0/[]).
      participant_count: Number(raw.participant_count ?? session.participant_count ?? 0),
      participants_summary: Array.isArray(raw.participants_summary)
        ? raw.participants_summary
        : (Array.isArray(session.participants_summary) ? session.participants_summary : []),
      lines_by_participant: Array.isArray(raw.lines_by_participant)
        ? raw.lines_by_participant
        : (Array.isArray(session.lines_by_participant) ? session.lines_by_participant : []),
      // "Pagar con POS": only present on controller/tableSessions.js#getActiveByTable's response
      // (undefined here means "this payload doesn't carry this info", NOT "no pending request" - see
      // upsertTableSession below, which only touches posPaymentRequestsByTable when this key exists,
      // so an unrelated socket event (tableSessionOrderUpdated, etc.) never wipes a banner it never
      // reported on in the first place).
      pos_payment_request: 'pos_payment_request' in raw
        ? raw.pos_payment_request
        : ('pos_payment_request' in session ? session.pos_payment_request : undefined),
      // Todas las solicitudes pendientes de la mesa (una por pedido/intento) y las sesiones abiertas que
      // comparten la mesa - solo las trae getActiveByTable; undefined = "este payload no lo reporta".
      pos_payment_requests: Array.isArray(raw.pos_payment_requests)
        ? raw.pos_payment_requests
        : (Array.isArray(session.pos_payment_requests) ? session.pos_payment_requests : undefined),
      open_session_uuids: Array.isArray(raw.open_session_uuids)
        ? raw.open_session_uuids
        : (Array.isArray(session.open_session_uuids) ? session.open_session_uuids : undefined),
      // Historial de actividad: solo lo trae getActiveByTable (undefined = "este payload no lo
      // reporta", no "sin eventos" - misma convencion que pos_payment_request).
      events: Array.isArray(raw.events)
        ? raw.events
        : (Array.isArray(session.events) ? session.events : undefined),
    };
  };

  const formatHistoryTime = (iso) => {
    const date = iso ? new Date(iso) : null;
    if (!date || Number.isNaN(date.getTime())) return null;
    return date.toLocaleTimeString("es-PA", { hour: "numeric", minute: "2-digit", hour12: true });
  };

  // Reemplaza el historial de una mesa con el que trae el backend y, la primera vez que la vemos,
  // da por vistos los eventos que ya existian (solo lo que llegue despues enciende el punto rojo).
  const setTableEvents = (tableId, events) => {
    if (!tableId) return;
    const key = String(tableId);
    setTableEventsByTable((prev) => ({ ...prev, [key]: events }));
    setSeenEventIdByTable((prev) => {
      if (prev[key] !== undefined) return prev;
      return { ...prev, [key]: events.length ? events[events.length - 1].id : null };
    });
  };

  const appendTableEvent = (tableId, event) => {
    if (!tableId || !event?.id) return;
    const key = String(tableId);
    setTableEventsByTable((prev) => {
      const current = prev[key] || [];
      if (current.some((item) => item.id === event.id)) return prev;
      return { ...prev, [key]: [...current, event] };
    });
    setSeenEventIdByTable((prev) => (prev[key] !== undefined ? prev : { ...prev, [key]: null }));
  };

  const clearTableEvents = (tableId) => {
    if (!tableId) return;
    const key = String(tableId);
    setTableEventsByTable((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSeenEventIdByTable((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const markTableEventsSeen = (tableId) => {
    if (!tableId) return;
    const key = String(tableId);
    const events = tableEventsByTable[key] || [];
    const lastId = events.length ? events[events.length - 1].id : null;
    setSeenEventIdByTable((prev) => (prev[key] === lastId ? prev : { ...prev, [key]: lastId }));
  };

  const isPendingSession = (session) => {
    const status = String(session?.status || "").toUpperCase();
    return ["PENDING", "REQUESTED", "PENDIENTE"].includes(status);
  };

  const isClosedSessionStatus = (status) => {
    const value = String(status || "").toUpperCase();
    return ["REJECTED", "CLOSED", "EXPIRED", "CANCELLED", "CANCELED", "FACTURADA", "INVOICED"].includes(value);
  };

  const getApiErrorMessage = (error, fallback = "No se pudo completar la accion") => (
    error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback
  );

  const payloadMatchesContext = (payload = {}) => {
    const session = payload.session || payload.table_session || payload;
    const payloadBranchId = session.branchoffice_id || payload.branchoffice_id || session.branchoffice?.id || payload.branchoffice?.id;
    const payloadBranchCode = session.branchoffice_code || payload.branchoffice_code || session.branchoffice?.code || payload.branchoffice?.code;
    const payloadBusinessId = session.business_id || payload.business_id || session.business?.id || payload.business?.id;
    const payloadBusinessCode = session.business_code || payload.business_code || session.business?.code || payload.business?.code;
    if (payloadBranchId && String(payloadBranchId) !== String(branchoffice?.id)) return false;
    if (payloadBranchCode && String(payloadBranchCode) !== String(branchoffice?.code)) return false;
    if (payloadBusinessId && String(payloadBusinessId) !== String(business?.id)) return false;
    if (payloadBusinessCode && String(payloadBusinessCode) !== String(business?.code)) return false;
    return true;
  };

  const upsertTableSession = (rawSession) => {
    const session = normalizeTableSession(rawSession);
    if (!session.session_uuid || !session.table_id) return;

    // "Pagar con POS": recovers/clears the pending-request banner from a fresh getActiveByTable
    // fetch (e.g. reopening the table after navigating away and back) - see the pos_payment_request
    // comment on normalizeTableSession above for why this only runs when the field was provided.
    // Con el backend nuevo viene la lista completa (todos los pedidos de la mesa); con uno viejo, la unica.
    const reportedRequests = session.pos_payment_requests !== undefined
      ? session.pos_payment_requests
      : (session.pos_payment_request !== undefined
        ? (session.pos_payment_request ? [session.pos_payment_request] : [])
        : undefined);
    if (reportedRequests !== undefined) {
      const tableKey = String(session.table_id);
      setPosPaymentRequestsByTable((prev) => {
        if (reportedRequests.length === 0) {
          if (!prev[tableKey]) return prev;
          const next = { ...prev };
          delete next[tableKey];
          return next;
        }
        return { ...prev, [tableKey]: reportedRequests };
      });
    }

    setTableSessionsByTable((prev) => {
      const next = { ...prev };
      if (isClosedSessionStatus(session.status)) {
        delete next[String(session.table_id)];
      } else {
        next[String(session.table_id)] = session;
      }
      return next;
    });

    if (isClosedSessionStatus(session.status)) {
      clearTableEvents(session.table_id);
    } else if (session.events !== undefined) {
      setTableEvents(session.table_id, session.events);
    }

    setTableSessionRequests((prev) => {
      const withoutCurrent = prev.filter((item) => item.session_uuid !== session.session_uuid);
      return isPendingSession(session) ? [session, ...withoutCurrent] : withoutCurrent;
    });
  };

  const removeTableSession = (rawSession) => {
    const session = normalizeTableSession(rawSession);
    setTableSessionRequests((prev) => prev.filter((item) => item.session_uuid !== session.session_uuid));
    if (session.table_id) {
      setTableSessionsByTable((prev) => {
        const next = { ...prev };
        delete next[String(session.table_id)];
        return next;
      });
      // Sesion cerrada/expirada/cancelada: no queda nada pendiente que revisar en esta mesa.
      clearTableAlert(session.table_id);
      clearPosPaymentRequest(session.table_id, { sessionUuid: session.session_uuid || null });
      // El historial de la mesa termina junto con la sesion. (Si la mesa tiene otro pedido abierto, el
      // refresco de la mesa que sigue a este cierre vuelve a traer su historial.)
      clearTableEvents(session.table_id);
    }
  };

  // ====== Fase 10: proteccion de peticiones en vuelo ======
  // Si ya hay una peticion en curso para la misma clave (p. ej. "fetchSession:12"), reutiliza esa
  // misma promesa en lugar de disparar otra en paralelo.
  const runDeduped = (key, fn) => {
    const inFlight = inFlightRequestsRef.current;
    const existing = inFlight.get(key);
    if (existing) return existing;

    const promise = Promise.resolve()
      .then(fn)
      .finally(() => {
        if (inFlight.get(key) === promise) inFlight.delete(key);
      });

    inFlight.set(key, promise);
    return promise;
  };

  // Firma estable de una orden para deduplicar snapshots de socket: mismo id + misma
  // shared_order_version (o updatedAt si la orden aun no tiene version, p. ej. antes de Fase 7).
  const getOrderSignature = (rawOrder) => {
    const id = rawOrder?._id?.$oid || rawOrder?._id || rawOrder?.id || rawOrder?.code || "";
    const version = rawOrder?.shared_order_version ?? rawOrder?.updatedAt ?? rawOrder?.updated_at ?? "";
    return `${id}:${version}`;
  };

  // Fase 2/6: aplica el snapshot de orden que ya viaja dentro del propio evento de socket
  // (`tableSessionOrderUpdated` incluye `order` completo - ver services/tableSessions.js#
  // emitTableSessionEvent y tableSessionOrderItems.js#finalizeMutation en el backend real), sin
  // pedirlo por GET. Devuelve true si pudo aplicar el snapshot (para que el llamador sepa que no
  // hace falta ademas un refreshTableOrdersAndDiners).
  const applyOrderSnapshotToTable = (tableId, rawOrder) => {
    if (!tableId || !rawOrder || !Array.isArray(rawOrder.groups)) return false;

    // "Pagar con POS": this snapshot's own payments[] is the live source of truth for whether a
    // physical-charge claim is still pending on this table - reflects it (or clears it, once
    // confirmed/released) on every tableSessionOrderUpdated broadcast, not just on the one-off
    // tableSessionPhysicalPaymentRequested event or a fresh getActiveByTable fetch (see
    // upsertTableSession above).
    const pendingPosPayments = (rawOrder.payments || []).filter(
      (p) => p.status === "PENDING" && p.channel === "POS_PHYSICAL"
    );
    const tableKeyForPos = String(tableId);
    const orderSessionUuid = rawOrder.table_session_uuid || null;
    const requestsFromThisOrder = pendingPosPayments.map((pendingPosPayment) => ({
      session_uuid: orderSessionUuid,
      payment_attempt_id: pendingPosPayment.payment_attempt_id,
      calculation_id: pendingPosPayment.calculation_id,
      amount: pendingPosPayment.amount,
      expires_at: pendingPosPayment.expires_at || null,
      participant: {
        participant_uuid: pendingPosPayment.participant_uuid,
        display_name: pendingPosPayment.participant_display_name || ""
      },
      items: (pendingPosPayment.line_uuids || []).map((lineUuid) => {
        const line = rawOrder.groups.find((g) => g.line_uuid === lineUuid)
          || (rawOrder.combos || []).find((c) => c.line_uuid === lineUuid);
        return {
          line_uuid: lineUuid,
          name: line?.name || "",
          price: line?.price || 0,
          amount: line?.amount || 1,
          unit_discount: line?.unit_discount || 0,
          taxes: line?.taxes || []
        };
      })
    }));
    // Un snapshot es de UN pedido: reemplaza solo las solicitudes de ese pedido y conserva las de los
    // otros pedidos de la mesa (antes un snapshot sin cobro pendiente borraba la solicitud de la mesa
    // aunque fuera de la otra orden).
    setPosPaymentRequestsByTable((prev) => {
      const list = prev[tableKeyForPos] || [];
      const others = list.filter((request) => request.session_uuid !== orderSessionUuid);
      const nextList = [...others, ...requestsFromThisOrder];
      const unchanged = nextList.length === list.length
        && nextList.every((request, index) => request.payment_attempt_id === list[index]?.payment_attempt_id);
      if (unchanged) return prev;
      const next = { ...prev };
      if (nextList.length > 0) next[tableKeyForPos] = nextList; else delete next[tableKeyForPos];
      return next;
    });

    const signature = getOrderSignature(rawOrder);
    // Clave por PEDIDO (mesa + id de orden): con dos pedidos en la mesa, el estado de lineas de uno no
    // puede servir de "anterior" para el otro - si no, cada snapshot alterno marcaba todas las lineas
    // del otro pedido como "nueva ronda" (aviso y alarma falsos).
    const tableKey = `${String(tableId)}:${String(rawOrder._id?.$oid || rawOrder._id || rawOrder.code || '')}`;
    const lastSignature = orderSignatureByTableRef.current.get(tableKey);
    if (lastSignature && lastSignature === signature) return true; // ya aplicado, nada que hacer

    // Fase 6: detecta lineas que acaban de pasar a SUBMITTED (nueva ronda enviada por un
    // participante) comparando contra el ultimo estado por linea que vimos para esta mesa.
    const previousStatuses = lineStatusByTableRef.current.get(tableKey) || new Map();
    const nextStatuses = new Map();
    const justSubmittedByParticipant = new Map();

    rawOrder.groups.forEach((g) => {
      const lineUuid = g.line_uuid || (g._id ? String(g._id?.$oid || g._id) : null);
      if (!lineUuid) return;
      const status = g.line_status || "SUBMITTED";
      nextStatuses.set(lineUuid, status);

      const wasKnown = previousStatuses.size > 0 && previousStatuses.has(lineUuid);
      const wasSubmittedOrLater = ["SUBMITTED", "PROCESSING", "SERVED"].includes(previousStatuses.get(lineUuid));
      if (previousStatuses.size > 0 && status === "SUBMITTED" && (!wasKnown || !wasSubmittedOrLater)) {
        const name = g.participant_display_name || "Cliente";
        justSubmittedByParticipant.set(name, (justSubmittedByParticipant.get(name) || 0) + 1);
      }
    });
    lineStatusByTableRef.current.set(tableKey, nextStatuses);

    if (justSubmittedByParticipant.size > 0) {
      const tableNumber = tablesRef.current.find((t) => t.id === tableId)?.number || tableId;
      justSubmittedByParticipant.forEach((count, name) => {
        AlertSmall(`Mesa ${tableNumber} · Nueva ronda: ${count} producto${count === 1 ? "" : "s"} · ${name}`, "info");
      });
      // Sonido + mesa resaltada, sin modal que haya que cerrar a mano - el toast de arriba (uno por
      // comensal) mas esto ya es suficiente aviso.
      startAlarm();
      markTableAlerted(tableId);
    }

    const [normalizedOrder] = normalizeBackendOrdersToTableOrders([rawOrder]);
    if (!normalizedOrder) return false;

    setTables((prev) =>
      prev.map((t) => {
        if (t.id !== tableId) return t;
        const others = (t.table_orders || []).filter((o) => String(o.id) !== String(normalizedOrder.id));
        const nextOrders = [normalizedOrder, ...others];
        const totalToPay = nextOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);
        return { ...t, table_orders: nextOrders, backend_orders: nextOrders, total: Number(totalToPay) };
      })
    );

    orderSignatureByTableRef.current.set(tableKey, signature);
    return true;
  };

  // Fase 10: agrupa eventos rapidos por mesa (varios "tableSessionOrderUpdated" seguidos, por
  // ejemplo) en una sola resolucion en vez de encadenar un GET por evento.
  const scheduleTableRefresh = (tableId, { refreshSession = true, refreshOrders = false } = {}) => {
    if (!tableId) return;
    const key = String(tableId);
    const timers = refreshDebounceRef.current;
    const pending = timers.get(key) || { refreshSession: false, refreshOrders: false, timeoutId: null };

    pending.refreshSession = pending.refreshSession || refreshSession;
    pending.refreshOrders = pending.refreshOrders || refreshOrders;

    if (pending.timeoutId) clearTimeout(pending.timeoutId);
    pending.timeoutId = setTimeout(() => {
      timers.delete(key);
      if (pending.refreshSession) fetchTableSessionByTable(tableId);
      if (pending.refreshOrders) refreshTableOrdersAndDiners(tableId);
    }, 200);

    timers.set(key, pending);
  };


  //====== Helper for login modal =======
  const controlModalLog = () => {
    const machine = JSON.parse(localStorage.getItem("machine")) || {};

    if (Number(machine.user_code_validation) === 1) {
      setShowLoginByCode(true);
    } else {
      setShowLoginByCode(false);
    }
  };

  //===== Backend helpers =====
  const normalizeBackendOrdersToTableOrders = (backendOrders = []) => {
    const groupSegmentsToModifiers = (segments = []) => {
      // backend segments: [{ segment_id, name, item: {name, item_id, price, ...} }]
      return segments.map((s) => ({
        segment_id: s.segment_id,
        name: s.segment_name || `Segmento ${s.segment_id}`, // si no viene nombre del segmento
        selections: [
          {
            item_id: s?.item?.item_id || null,
            name: s?.item?.name || s?.name || "",
            price: Number(s?.item?.price || 0),
            img: s?.item?.img || null,
            description: s?.item?.description || null,
            segment_id: s.segment_id,
          },
        ],
      }));
    };

    return (backendOrders || []).map((ord) => ({
      id: ord._id || ord.id || ord.code,
      transaction_code: ord.transaction_code || String(ord._id || ord.id).slice(-6),
      status: ord.status_id === 1 ? "open" : String(ord.status_id),
      order_source: ord.order_source || ord.source || ord.orderSource || null,
      table_session_uuid: ord.table_session_uuid || ord.session_uuid || null,
      // Fase 2/5: contrato real confirmado en app/models/temp_orders.js del backend - version
      // optimista de la orden colaborativa, usada para deduplicar snapshots de socket.
      shared_order_version: Number(ord.shared_order_version || 1),
      created_at: ord.createdAt || new Date().toISOString(),
      total: Number(ord.total || 0),
      // Numero de fidelizacion (DonLee "Paisanitos" hoy) que el cliente vinculo desde el carrito del
      // ecommerce antes de pagar - ver setSessionClientPhone en el backend. Placeholders vacios del
      // resto del flujo ("0"/"00") no cuentan como "vinculado".
      client_phone: ["", "0", "00", null, undefined].includes(ord.client_phone) ? "" : String(ord.client_phone),
      items: (ord.groups || []).map((g) => ({
        signature: `backend:${ord._id || ord.code}:${g.segments_string || g.group_id}`, // unique per config
        group_id: g.group_id,
        name: g.name,
        qty: Number(g.amount || 1),
        base_price: Number(g.price || 0),
        price: Number(g.price || 0), // unit price
        diner_id: g.diner_id || null,
        diner_name: g.diner_name || "",
        modifiers: Array.isArray(g.segments) && g.segments.length > 0 ? groupSegmentsToModifiers(g.segments) : [],
        modifiers_total: 0,
        // Fase 5: campos de pedido colaborativo confirmados en app/models/temp_orders.js (grupo
        // groups[]) del backend. Para lineas agregadas por el POS quedan en sus defaults de schema
        // (line_uuid null, participant_uuid null, line_status "SUBMITTED"), que ya representan
        // correctamente "linea confirmada, sin participante" - no se inventa nada aqui.
        line_uuid: g.line_uuid || (g._id ? String(g._id?.$oid || g._id) : null),
        participant_uuid: g.participant_uuid || null,
        participant_display_name: g.participant_display_name || "",
        line_status: g.line_status || "SUBMITTED",
        submitted_at: g.submitted_at || null,
        revision: Number(g.revision || 1),
      })),
    }));
  };

  const normalizeBackendDiners = (backendDiners = []) => {
    return (backendDiners || []).map((d) => {
      const normalizedOrders = (d.items || []).map((item, index) => ({
        id: `${item.source || "item"}_${index}_${item.group_id || item.combo_id || ""}`,
        source: item.source || "group",
        name: item.name || "Producto",
        qty: Number(item.qty || 1),
        price: Number(item.price || 0),
        emoji: item.source === "combo" ? "🥡" : "🍽️",
        total: Number(item.total || 0),
        order_id: item.order_id,
        group_id: item.group_id || null,
        combo_id: item.combo_id || null,
      }));

      return {
        diner_id: d.diner_id,
        name: d.name || "Comensal",
        seat_number: d.seat_number ?? null,
        selected_order_id: d.order_id || d.orders?.[0] || normalizedOrders.find((x) => x.order_id)?.order_id || null,
        orders: normalizedOrders,
      };
    });
  };

  const refreshTableOrdersAndDiners = async (tableId) => {
    if (!tableId) return;
    return runDeduped(`refreshOrders:${tableId}`, async () => {
      try {
        const [ordersResp, dinersResp] = await Promise.all([
          // controller.getTempOrdersByTable (smartpay-dashboard) answers with HTTP 400 "Esta mesa no
          // tiene ordenes temporales" instead of 200 + [] once the table's last temp order is gone
          // (e.g. right after it gets paid/closed) - axios throws on that, which would otherwise land
          // in the catch below and leave table_orders stuck showing the just-closed order forever.
          // Treat that specific case as a legitimate empty list instead of a failure.
          TablesFetchAPI.getTempOrdersByTableId(tableId, token).catch((err) => {
            if (err?.response?.status === 400) return { data: { orders: [] } };
            throw err;
          }),
          TablesFetchAPI.getTableDinners(tableId, token),
        ]);
        const tableOrders = normalizeBackendOrdersToTableOrders(ordersResp?.data?.orders || []);
        const normalizedDiners = normalizeBackendDiners(dinersResp?.data?.diners || []);
        const totalToPay = tableOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);

        // Mantiene sincronizada la firma de version usada por applyOrderSnapshotToTable, para que un
        // proximo evento de socket con la misma version no repita trabajo.
        tableOrders.forEach((tableOrder) => {
          orderSignatureByTableRef.current.set(`${String(tableId)}:${String(tableOrder.id)}`, getOrderSignature({
            _id: tableOrder.id,
            shared_order_version: tableOrder.shared_order_version
          }));
        });

        setTables((prev) =>
          prev.map((t) =>
            t.id !== tableId
              ? t
              : {
                ...t,
                total: Number(totalToPay),
                table_orders: tableOrders,
                backend_orders: tableOrders,
                comensales: normalizedDiners,
                guests: normalizedDiners.length,
                orders_loading: false,
                orders_error: null,
              }
          )
        );
      } catch (err) {
        setTables((prev) =>
          prev.map((t) =>
            t.id === tableId
              ? { ...t, orders_loading: false, orders_error: getApiErrorMessage(err, "Error cargando ordenes") }
              : t
          )
        );
      }
    });
  };

  const fetchTableSessionByTable = async (tableId) => {
    if (!tableId) return;
    return runDeduped(`fetchSession:${tableId}`, async () => {
      try {
        const response = await TableSessionsFetchAPI.getActiveSessionByTable(
          tableId,
          token
        );
        const session = response?.data?.data || response?.data?.session || response?.data?.table_session;
        if (session) {
          upsertTableSession(session);
        } else {
          removeTableSession({ table_id: tableId });
        }
      } catch (error) {
        if (error?.response?.status === 404) removeTableSession({ table_id: tableId });
      }
    });
  };

  const fetchTableSessions = async (tableList = tables) => {
    return runDeduped('fetchAllSessions', async () => {
      try {
        const pendingResp = await TableSessionsFetchAPI.getPendingSessions(
          { branchoffice_code: branchoffice?.code, status: "PENDING", limit: 100 },
          token
        );
        const pendingData = pendingResp?.data?.data;
        const pendingSource = Array.isArray(pendingData)
          ? pendingData
          : pendingData?.items || pendingResp?.data?.items || [];
        const pending = pendingSource.map(normalizeTableSession).filter((session) => session.session_uuid);
        const active = (await Promise.all(
          (tableList || []).map(async (table) => {
            try {
              const response = await TableSessionsFetchAPI.getActiveSessionByTable(table.id, token);
              return response?.data?.data ? normalizeTableSession(response.data.data) : null;
            } catch (error) {
              return null;
            }
          })
        )).filter((session) => session?.session_uuid && session.table_id);

        setTableSessionRequests(pending.filter(isPendingSession));
        active.forEach((session) => {
          if (!isClosedSessionStatus(session.status) && session.events !== undefined) {
            setTableEvents(session.table_id, session.events);
          }
        });
        setTableSessionsByTable(() => {
          const next = {};
          [...active, ...pending].forEach((session) => {
            if (!isClosedSessionStatus(session.status) && session.table_id) {
              next[String(session.table_id)] = session;
            }
          });
          return next;
        });
        pending.forEach((session) => notifiedSessionRequestsRef.current.add(session.session_uuid));
      } catch (error) {
        ErrorAlert(getApiErrorMessage(error, "No se pudieron cargar las solicitudes de mesa"), "Solicitudes de mesa", "warning");
      }
    });
  };


  // ===== HANDLERS =====
  const selectZone = async (zoneId) => {
    setCurrentZone(zoneId);
    setEditMode(false);
    setSelectedTableId(null);
    setSelectedObjectId(null);
    setZoomLevel((z) => (zoneId === "all" ? 1 : z));
  };

  const toggleEditMode = () => setEditMode((v) => !v);

  const handleZoomChange = (z) => setZoomLevel(z);

  const closeTableOffcanvas = () => setIsTableOffcanvasOpen(false);

  const handleSelectTable = async (id) => {
    setSelectedTableId(id);
    setSelectedObjectId(null);
    setIsTableOffcanvasOpen(true);
    // Abrir el detalle de la mesa cuenta como "ya lo vi": apaga su punto rojo y silencia la alarma.
    clearTableAlert(id);
    stopAlarm();
  };

  const handleSelectObject = (id) => {
    setSelectedObjectId(id);
    setSelectedTableId(null);
    setIsTableOffcanvasOpen(true);
  };

  const handleMoveTable = (id, x, y) => {
    setTables((prev) => prev.map((t) => (t.id === id ? { ...t, gridX: x, gridY: y } : t)));
  };

  const handleMoveObject = (id, x, y) => {
    setObjects((prev) => prev.map((o) => (o.id === id ? { ...o, gridX: x, gridY: y } : o)));
  };

  const handleCellClick = (x, y) => {

    if (!editMode) return;
    handleCreateTableAt(x, y);
  };

  // ====== CRUD COMENSALES / ORDERS ======
  const recalcTableTotals = (t) => {
    const comensales = t.comensales || [];
    let total = 0;

    comensales.forEach((c) => {
      (c.orders || []).forEach((o) => {
        total += (Number(o.price) || 0) * (Number(o.qty) || 1);
      });
    });

    return {
      ...t,
      comensales,
      guests: comensales.length,
      total,
      orders: comensales.flatMap((c) => c.orders || []),
    };
  };

  const addComensalToTable = (tableId, name) => {
    updateTable(tableId, (t) => {
      const comensales = [...(t.comensales || [])];
      comensales.push({ name: name?.trim() || `Cliente ${comensales.length + 1}`, orders: [] });
      return recalcTableTotals({ ...t, comensales });
    });
  };

  const removeComensalFromTable = (tableId, comensalIndex) => {
    updateTable(tableId, (t) => {
      const comensales = [...(t.comensales || [])];
      comensales.splice(comensalIndex, 1);
      return recalcTableTotals({ ...t, comensales });
    });
  };

  const addOrderToComensal = (tableId, comensalIndex, order) => {
    updateTable(tableId, (t) => {
      const comensales = [...(t.comensales || [])];
      const c = { ...(comensales[comensalIndex] || { name: "Cliente", orders: [] }) };
      const orders = [...(c.orders || [])];

      orders.push({
        name: order?.name?.trim() || "Platillo",
        price: Number(order?.price) || 0,
        qty: Math.max(1, Number(order?.qty) || 1),
        emoji: order?.emoji || "🍽️",
      });

      c.orders = orders;
      comensales[comensalIndex] = c;

      return recalcTableTotals({ ...t, comensales });
    });
  };

  const removeOrderFromComensal = (tableId, comensalIndex, orderIndex) => {
    updateTable(tableId, (t) => {
      const comensales = [...(t.comensales || [])];
      const c = { ...(comensales[comensalIndex] || { name: "Cliente", orders: [] }) };
      const orders = [...(c.orders || [])];

      orders.splice(orderIndex, 1);
      c.orders = orders;
      comensales[comensalIndex] = c;

      return recalcTableTotals({ ...t, comensales });
    });
  };


  const setTablePaid = (tableId, paidValue) => {
    updateTable(tableId, (t) => {
      const total = Number(t.total || 0);
      let paid = Number(paidValue || 0);
      if (paid < 0) paid = 0;
      if (paid > total) paid = total;
      return { ...t, paid };
    });
  };

  const addTablePayment = (tableId, amountToAdd) => {
    updateTable(tableId, (t) => {
      const total = Number(t.total || 0);
      const currentPaid = Number(t.paid || 0);
      const add = Number(amountToAdd || 0);
      let nextPaid = currentPaid + add;

      if (nextPaid < 0) nextPaid = 0;
      if (nextPaid > total) nextPaid = total;

      return { ...t, paid: nextPaid };
    });
  };

  //Function to create a new table at specific coordinates (used in edit mode)
  const handleCreateTableAt = async (x, y, payload) => {
    // id incremental seguro
    const nextId =
      tables.length > 0 ? tables.length + 1 : 1;

    const newTable = {
      id: nextId,
      name: String(nextId), //For backend we need a name, but for frontend we will use number = name
      branchoffice_id: branchoffice?.id, //For backend we need to send the branchoffice_id to create the table, but for frontend we will use zone = environment
      branchoffice_environment_id: currentZone,//For backend, zone = environment
      number: String(nextId),
      zone: currentZone,
      chairs: 4,
      shape: "square",
      status: "available", // ✅ default
      gridX: x,
      gridY: y,
      posx: x, //For backend we need to send posx and posy, but for frontend we will use gridX and gridY
      posy: y, //For backend we need to send posx and posy, but for frontend we will use gridX and gridY
      gridW: 1,
      gridH: 1,
      total: 0,
      paid: 0,
      time: "",
      comensales: [], // ✅ para que no rompa tabs
      table_orders: [], // ✅ para que no rompa tabs
    };
    // We check if the new table can be placed in the current coordinates, if not we find the first free spot in the zone
    if (currentZone === "all" || payload !== undefined) {
      newTable.zone = Number(payload.zone) || Number(zones[0]?.id);
      newTable.branchoffice_environment_id = Number(payload.zone) || Number(zones[0]?.id);
      newTable.gridX = 0;
      newTable.gridY = 0;
      newTable.posx = 0;
      newTable.posy = 0;
      newTable.gridH = payload.gridH || 1;
      newTable.gridW = payload.gridW || 1;
      newTable.chairs = payload.chairs || 4;
      newTable.shape = payload.shape || "square";
      newTable.name = payload.number ? String(payload.number) : String(newTable.id);
      newTable.number = payload.number ? String(payload.number) : String(newTable.id);
    };



    let createdTable = await TablesFetchAPI.createTable(newTable, token);
    if (!createdTable?.data?.table) return;
    // We update the newTable with the id and other data from backend
    newTable.id = createdTable.data.table.data.insertId;

    setTables((prev) => [...prev, newTable]);

    // ✅ selecciona y abre offcanvas de una vez
    setSelectedTableId(newTable.id);
    setSelectedObjectId(null);
    setIsTableOffcanvasOpen(true);
  };


  const handleCreatePlantObject = () => {
    if (currentZone === "all") return;

    const zone = zones.find((z) => z.id === currentZone);
    const spot = findFirstFreeSpot(zone, tables, objects, 1, 1);
    if (!spot) return;

    const nextId =
      objects.length > 0 ? Math.max(...objects.map((o) => Number(o.id))) + 1 : 1;

    const newObj = {
      id: nextId,
      type: "plant",
      zone: currentZone,
      gridX: spot.x,
      gridY: spot.y,
      gridW: 1,
      gridH: 1,
      label: "",
    };

    setObjects((prev) => [...prev, newObj]);
    setSelectedObjectId(nextId);
    setSelectedTableId(null);

    // opcional: si quieres forzar editMode al crear objetos:
    // setEditMode(true);
  };

  // ====== CRUD branchoffice environments ======
  const getBranchofficeEnvironments = async () => {
    try {
      let response = await BranchOfficeEnvironmentsFetchAPI.getBranchofficeEnvironments(branchoffice?.id, token);
      // We set the environments to zones
      setZones(response.data.data.map(env => ({
        id: env.id,
        name: env.name,
        color: env.color,
        cols: env.grid_cols,
        rows: env.grid_rows
      })));
    }
    catch (e) {
      setZones([]);
    }
  };

  const updateBranchOfficeEnvironment = async (environmentId, data) => {
    try {
      let response = await BranchOfficeEnvironmentsFetchAPI.updateBranchofficeEnvironmentDetails(data, environmentId, token);

      return response;
    } catch (e) {
      ErrorAlert("Error al actualizar la zona", e.message || "Ocurrió un error al actualizar la zona");
    }
  }

  const deleteBranchOfficeEnvironment = async (environmentId) => {
    try {
      await BranchOfficeEnvironmentsFetchAPI.deleteBranchofficeEnvironment(environmentId, token);
      return true;
    } catch (e) {
      ErrorAlert("Debe eliminar todas las mesas de la zona, antes de eliminar la zona", "Advertencia", "warning");
      return false;
    }
  }

  // ====== CRUD branchoffice tables ======
  const getBranchofficeTables = async () => {
    try {
      let response = await TablesFetchAPI.getBranchofficeTables(branchoffice?.id, token);
      // We set the tables
      const normalizedTables = response.data.tables.map(table => ({
        id: table.id,
        number: table.name,
        zone: table.branchoffice_environment_id, // We will assign zones later
        chairs: table.chairs,
        shape: table.shape || "square",
        status: table.status,
        gridX: table.posx,
        gridY: table.posy,
        gridW: table.gridW,
        gridH: table.gridH,
        total: 0,// We will calculate totals later
        paid: 0,
        time: "45:30",
        status_since: table.status_since || null,
        comensales: [],
        orders: [],
        backend_orders: normalizeBackendOrdersToTableOrders(table.orders || []),
        guests: table.guests || 0,
        reservation_id: table.reservation_id || null,
        table_orders: normalizeBackendOrdersToTableOrders(table.orders || []), // Aquí guardamos las órdenes reales del backend para mostrarlas en el offcanvas, pero no las mezclamos con los comensales porque el backend no tiene esa relación, solo asigna las órdenes a la mesa, no a los comensales
      }));
      setTables(normalizedTables);
      fetchTableSessions(normalizedTables);
    } catch (e) {
      setTables([]);
    }
  };

  const updateTable = async (tableId, data) => {
    try {
      //We call the API to update the table, then we update the state with the new data
      await TablesFetchAPI.updateTable(tableId, data, token);
    } catch (err) {
      ErrorAlert("Error al actualizar la mesa", err.message || "Ocurrió un error al actualizar la mesa");
    }
  };

  const deleteTable = async (tableId) => {
    try {
      await TablesFetchAPI.deleteTable(tableId, token);
      setTables(prev => prev.filter(t => t.id !== tableId));
    } catch (err) {
      ErrorAlert("Error al eliminar la mesa", "No se pudo eliminar la mesa. Intenta de nuevo.", "error")
    }
  }

  //====== CRUD branchoffice environments objects ======
  const getEnvironmentsObjectsByBranchofficeId = async () => {
    try {
      let response = await TablesFetchAPI.getEnvironmentsObjectsByBranchofficeId(branchoffice?.id, token);

      console.log("objetos del ambiente:", response);
      // We set the objects
      setObjects(response.data.environment_objects.map(obj => ({
        id: obj.id,
        type: obj.type,
        zone: Number(obj.branchoffice_environment_id), // We will assign zones later
        gridX: obj.posx,
        gridY: obj.posy,
        gridW: obj.gridW,
        gridH: obj.gridH,
        label: obj.type === "plant" ? "🌱" : obj.type === "wall-h" || obj.type === "wall-v" ? "🧱" : "",
      })));
    } catch (e) {
      setObjects([]);
    }
  };

  const deleteObject = async (objectId) => {
    try {
      await TablesFetchAPI.deleteEnvironmentObject(objectId, token);
      setObjects(prev => prev.filter(o => o.id !== objectId));
    } catch (err) {
      ErrorAlert("Error al eliminar el objeto", "No se pudo eliminar el objeto. Intenta de nuevo.", "error")
    }
  }

  const updateObject = async (objectId, data) => {
    try {
      await TablesFetchAPI.updateEnvironmentObject(objectId, data, token);
    } catch (err) {
      ErrorAlert("Error al actualizar el objeto", err.message || "Ocurrió un error al actualizar el objeto");
    }
  }

  const createObject = async (data) => {
    try {
      // id incremental seguro
      const nextId =
        objects.length > 0 ? objects.length + 1 : 1;

      const newObj = {
        id: nextId,
        type: data.type,
        zone: data.zone,
        gridX: data.posx,
        branchoffice_id: branchoffice?.id, //For backend we need to send the branchoffice_id to create the table, but for frontend we will use zone = environment
        gridY: data.posy,
        posx: data.posx,
        posy: data.posy,
        gridW: data.gridW,
        gridH: data.gridH,
        label: data.type === "plant" ? "🌱" : data.type === "wall-h" || data.type === "wall-v" ? "🧱" : "",
        zone: currentZone,
        branchoffice_environment_id: currentZone
      }

      // We check if the new table can be placed in the current coordinates, if not we find the first free spot in the zone
      if (currentZone === "all") {
        newObj.zone = Number(data.zone) || Number(zones[0]?.id);
        newObj.branchoffice_environment_id = Number(data.zone) || Number(zones[0]?.id);
      };

      let response = await TablesFetchAPI.createEnvironmentObject(newObj, token);
      if (!response?.data?.environment_object) throw new Error("No se pudo crear el objeto");
      const obj = response.data.environment_object.insertId;
      setObjects(prev => [...prev, {
        id: obj,
        type: data.type,
        zone: Number(newObj.zone),
        gridX: data.posx,
        gridY: data.posy,
        posx: data.posx,
        posy: data.posy,
        gridW: data.gridW,
        gridH: data.gridH,
        label: data.type === "plant" ? "🌱" : data.type === "wall-h" || data.type === "wall-v" ? "🧱" : "",
      }]);
    } catch (err) {
      console.error("Error creando objeto:", err);
      ErrorAlert("Error al crear el objeto", err.message || "Ocurrió un error al crear el objeto");
    }
  }

  //====== CRUD RESERVATIONS ======
  const createClientReservation = async (data) => {
    try {
      let response = await ReservationFetchAPI.createReservationClient(data, token);
      return response;
    } catch (e) {
      ErrorAlert({ title: "Error al crear reserva", text: e.message || "Ocurrió un error al crear la reserva" });
    }
  }
  //Function to get the reservation info by reservation id, this will be used to show the reservation info in the table offcanvas when a table with reservation is selected
  const getReservationData = async (reservation_id) => {
    try {
      const res = await ReservationFetchAPI.getReservationClientById(reservation_id, token);
      console.log("peticion de la reserva:", res);
      return res.data.reservation || null;
    } catch (error) {
      console.error("Error fetching reservation data:", error);
      return null;
    }
  };


  //========CRUD Orders========= 

  //Function to create a temporary order for a table, this will be used in the reservation flow when we want to create an order for a reserved table, this order will not be saved in the backend until the reservation is confirmed and the table is occupied, this is just to show the order in the UI and calculate totals, etc.
  const createTempOrderForTable = async ({ tableId, items }) => {

    if (!tableId || !items?.length) return null;

    // Replace with your real API call
    const createdOrder = {
      id: `tmp_${Date.now()}`,
      table_id: tableId,
      status: "open",
      created_at: new Date().toISOString(),
      items,
      total: items.reduce((s, it) => s + Number(it.price || 0) * Number(it.qty || 1), 0),
    };

    setTables((prev) =>
      prev.map((t) =>
        t.id !== tableId
          ? t
          : {
            ...t,
            table_orders: [createdOrder, ...(t.table_orders || [])],
            backend_orders: [createdOrder, ...(t.backend_orders || [])],
          }
      )
    );

    return createdOrder;
  };

  const handleApproveTableSession = async (session) => {
    if (!session?.session_uuid || processingSessionId) return;
    controlModalLog();
    setProcessingSessionId(session.session_uuid);
    try {
      const response = await TableSessionsFetchAPI.approveSession(
        session.session_uuid,
        {},
        token
      );
      const confirmedSession = response?.data?.session || response?.data?.table_session || response?.data?.data || { ...session, status: "APPROVED" };
      upsertTableSession(confirmedSession);
      setTableSessionRequests((prev) => prev.filter((item) => item.session_uuid !== session.session_uuid));
      await fetchTableSessionByTable(session.table_id);
      ErrorAlert("Solicitud aprobada correctamente", "Mesa autorizada", "success");
    } catch (error) {
      if (error?.response?.status === 409) {
        await fetchTableSessions();
        if (session?.table_id) await refreshTableOrdersAndDiners(session.table_id);
      }
      ErrorAlert(getApiErrorMessage(error, "No se pudo aprobar la solicitud"), "Solicitud de mesa", "error");
    } finally {
      setProcessingSessionId(null);
    }
  };

  const handleRejectTableSession = async (session, reason) => {
    if (!session?.session_uuid || processingSessionId) return;
    controlModalLog();
    setProcessingSessionId(session.session_uuid);
    try {
      await TableSessionsFetchAPI.rejectSession(
        session.session_uuid,
        { reason },
        token
      );
      removeTableSession({ ...session, status: "REJECTED" });
      ErrorAlert("Solicitud rechazada", "Solicitudes de mesa", "success");
    } catch (error) {
      if (error?.response?.status === 409) await fetchTableSessions();
      ErrorAlert(getApiErrorMessage(error, "No se pudo rechazar la solicitud"), "Solicitudes de mesa", "error");
    } finally {
      setProcessingSessionId(null);
    }
  };

  const handleCloseTableSession = (table) => {
    const session = table?.tableSession;
    if (!session?.session_uuid || processingSessionId) return;
    const hasOpenOrder = Boolean(session.has_active_order || session.temp_order_id || (table?.table_orders || []).length > 0);
    onOpenModal("close-table-session", { table, hasOpenOrder });
  };

  // Mover la sesion e-commerce de una mesa a otra libre (el cliente se cambio de lugar): conserva pedido,
  // pagos y el mismo QR - ver moveSessionToTable en el backend.
  const handleMoveTableSession = (table) => {
    const session = table?.tableSession;
    if (!session?.session_uuid || processingSessionId) return;
    onOpenModal("move-table-session", { table });
  };

  // Funcion (no useMemo): lee `modal`, que se declara mas abajo - evaluarla al renderizar evita tocarla
  // antes de su declaracion (eso dejaba la pantalla en blanco).
  const getMoveTargetCandidates = () => {
    const fromId = modal?.payload?.table?.id;
    return tables
      .filter((t) => (
        String(t.id) !== String(fromId)
        && String(t.status || "available").toLowerCase() === "available"
        && !tableSessionsByTable[String(t.id)]
      ))
      .map((t) => {
        const zoneName = zones.find((z) => String(z.id) === String(t.zone))?.name;
        return { id: t.id, label: `Mesa ${t.number}${zoneName ? ` · ${zoneName}` : ""}` };
      });
  };

  // Estado de las DOS mesas tras mover una sesion (lo usan el evento tableSessionMoved del socket y la propia
  // caja que movio, sin esperar al socket). Idempotente: un movimiento con varios pedidos/sesiones en la
  // mesa llega como varios eventos y todos terminan en el mismo estado. Rendimiento: solo se refrescan LAS
  // DOS mesas afectadas (scheduleTableRefresh junta las llamadas repetidas en una sola) - nunca la lista
  // completa de sesiones de la sucursal.
  const applyTableMoveLocally = (fromId, toId) => {
    if (fromId) {
      setTableSessionsByTable((prev) => {
        if (!prev[String(fromId)]) return prev;
        const next = { ...prev };
        delete next[String(fromId)];
        return next;
      });
      clearTableAlert(fromId);
      clearPosPaymentRequest(fromId);
    }
    setTables((prev) => prev.map((t) => {
      if (fromId && String(t.id) === String(fromId)) return { ...t, status: "available", status_since: null, table_orders: [], total: 0 };
      if (toId && String(t.id) === String(toId)) return { ...t, status: "occupied", status_since: t.status === "occupied" && t.status_since ? t.status_since : new Date().toISOString() };
      return t;
    }));
    scheduleTableRefresh(toId, { refreshSession: true, refreshOrders: true });
    scheduleTableRefresh(fromId, { refreshSession: true, refreshOrders: true });
  };

  const confirmMoveTableSession = async (targetTableId) => {
    const table = modal?.payload?.table;
    const session = table?.tableSession;
    if (!session?.session_uuid || !targetTableId || processingSessionId) return;
    setProcessingSessionId(session.session_uuid);
    try {
      await TableSessionsFetchAPI.moveTable(session.session_uuid, targetTableId, token);
      setModal(null);
      controlModalLog();
      // El evento tableSessionMoved (socket) mueve el estado de las dos mesas en todas las cajas; esto
      // deja a ESTA caja al dia al instante aunque el socket llegue tarde. Antes aqui se recargaban las
      // sesiones de TODAS las mesas de la sucursal (una peticion por mesa) y luego las dos mesas una tras
      // otra, esperando cada una: era lo que hacia sentir lenta la operacion. Ahora es solo un refresco
      // en segundo plano de las dos mesas afectadas.
      applyTableMoveLocally(table.id, targetTableId);
      setSelectedTableId(targetTableId);
      ErrorAlert("Cliente movido de mesa correctamente", "Sesion e-commerce", "success");
    } catch (error) {
      ErrorAlert(getApiErrorMessage(error, "No se pudo mover la sesion de mesa"), "Sesion e-commerce", "error");
    } finally {
      setProcessingSessionId(null);
    }
  };

  const confirmCloseTableSession = async (reason) => {
    const table = modal?.payload?.table;
    const session = table?.tableSession;
    if (!session?.session_uuid || processingSessionId) return;
    setModal(null);
    controlModalLog();
    setProcessingSessionId(session.session_uuid);
    try {
      await TableSessionsFetchAPI.closeSession(
        session.session_uuid,
        { reason },
        token
      );
      await fetchTableSessionByTable(table.id);
      await refreshTableOrdersAndDiners(table.id);
      ErrorAlert("Sesion cerrada correctamente", "Sesion e-commerce", "success");
    } catch (error) {
      if (error?.response?.status === 409) {
        await fetchTableSessions();
        await refreshTableOrdersAndDiners(table.id);
      }
      ErrorAlert(getApiErrorMessage(error, "No se pudo cerrar la sesion"), "Sesion e-commerce", "error");
    } finally {
      setProcessingSessionId(null);
    }
  };



  // ===== UTIL =====
  const safeSlug = (name) =>
    String(name || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");


  const [modal, setModal] = useState(null);

  // helper para abrir
  const onOpenModal = (name, payload = {}) => setModal({ name, payload });

  // Mantiene tablesRef sincronizado con el estado tables, para poder leerlo desde closures que no
  // se re-crean en cada render (el listener de socket, ver mas abajo).
  useEffect(() => {
    tablesRef.current = tables;
  }, [tables]);

  //Use Effect to charge branchoffice environments, tables, etc.
  useEffect(() => {
    //Fetch environments, tables, etc. here
    getBranchofficeEnvironments();
    getBranchofficeTables();
    getEnvironmentsObjectsByBranchofficeId();
    fetchTableSessions();
    // Cuando la maquina entra directo a Mesas (startup_view = TABLES) nunca se monta PaymentPost.jsx,
    // que es quien normalmente carga "generalData" (productos/grupos) en localStorage. Sin esto los
    // modales que arman ordenes (RfmModals.jsx) no encuentran productos.
    ensureGeneralData();
  }, []);

  //Use effect to open login modal
  useEffect(() => {
    const machine = JSON.parse(localStorage.getItem("machine")) || {};

    if (Number(machine.user_code_validation) === 1) {
      setShowLoginByCode(true);
    }
  }, []);

  //Hook to fetch orders of the selected table when selectedTableId changes
  useEffect(() => {
    if (!selectedTableId) return;

    let cancelled = false;

    const run = async () => {
      let dinners = []
      try {
        // loading flag opcional
        setTables((prev) =>
          prev.map((t) =>
            t.id === selectedTableId ? { ...t, orders_loading: true, orders_error: null } : t
          )
        );

        // Fecth orders from backend
        const resp = await TablesFetchAPI.getTempOrdersByTableId(selectedTableId, token);
        if (cancelled) return;

        if (!resp?.data?.success) throw new Error(resp?.data?.message || "No se pudieron cargar las órdenes");

        try {
          const respDinners = await TablesFetchAPI.getTableDinners(selectedTableId, token);

          dinners = normalizeBackendDiners(respDinners.data.diners || []);
        } catch (e) {
          dinners = []
        }

        const tableOrders = normalizeBackendOrdersToTableOrders(resp.data.orders || []);
        const totalToPay = tableOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);
        setTables((prev) =>
          prev.map((t) =>
            t.id !== selectedTableId
              ? t
              : {
                ...t,
                total: Number(totalToPay),
                table_orders: tableOrders,
                backend_orders: tableOrders,
                comensales: dinners,
                guest: dinners.length,
                orders_loading: false,
                orders_error: null,
              }
          )
        );
      } catch (err) {
        if (cancelled) return;

        setTables((prev) =>
          prev.map((t) =>
            t.id !== selectedTableId
              ? t
              : {
                ...t,
                orders_loading: false,
                orders_error: err?.message || "Error cargando órdenes",
              }
          )
        );
      }
    };

    run();
    fetchTableSessionByTable(selectedTableId);

    return () => {
      cancelled = true;
    };
  }, [selectedTableId]);

  // El contador de tiempo de cada mesa lo dibuja <ElapsedClock> (hooks/useNowTick.js): antes un setInterval
  // de 1s aqui guardaba Date.now() en el estado de esta pantalla y la redibujaba ENTERA cada segundo (todas las
  // mesas, el panel lateral y los modales). En Android, tras horas de uso, eso saturaba el hilo principal y la
  // app dejaba de responder - solo cambian unos textos mm:ss, ahora solo se redibujan esos.

  useEffect(() => {
    if (!branchoffice?.code || !business?.code) return undefined;
    // IMPORTANT: the backend emits tableSessionRequested/Approved/etc to the room
    // `business_{business_code}_branchoffice_{branchoffice_code}`. Sending machine_code
    // here would join a different room (machine_..._room) and these events would never
    // arrive, so this connection must match ModalReservation's query exactly.
    const socket = Socket({ business_code: business?.code, branchoffice_code: branchoffice?.code });
    // Fase 2/11: seguimos escuchando unicamente en la sala business_{code}_branchoffice_{code} (el
    // patron actual del POS - una sola conexion, sin session_uuid en la query). Los eventos que el
    // backend real solo emite a la sala table_session_{uuid} (tableSessionParticipantJoined,
    // tableSessionInviteConsumed/Revoked, tableSessionSharedOrderUpdated, tableSessionItemsSubmitted)
    // son intencionalmente inalcanzables desde aqui - ver sockets.js#joinTableSessionRoom en el
    // backend, que deliberadamente nunca une un socket a esa sala a partir de un session_uuid
    // recibido por query, exactamente la restriccion que este modulo ya debia respetar. En su lugar,
    // "tableSessionOrderUpdated" (que SI llega a esta sala) ya trae la orden completa en cada mutacion
    // colaborativa (ver services/tableSessions.js#emitTableSessionEvent y
    // tableSessionOrderItems.js#finalizeMutation en el backend), asi que es la señal real que usamos.
    const sessionEvents = [
      "tableSessionRequested",
      "tableSessionApproved",
      "tableSessionRejected",
      "tableSessionActivated",
      "tableSessionClosed",
      "tableSessionExpired",
      "tableSessionCancelled",
      "tableSessionOrderUpdated",
      "newTableSessionRequest",
      "tableSessionPaymentRequested",
      // Pre-pay only (see payload.payment_mode below) - the customer self-authorizes payment with
      // no waiter involved, so unlike post-pay (where this event just confirms the waiter's own
      // "Habilitar pago en linea" tap) it needs to actively alert staff here.
      "tableSessionPaymentAuthorized",
      // "Pagar con POS": customer asked to pay a specific selection of lines on the physical
      // terminal instead of an online gateway - see claimLinesForPayment on the backend.
      "tableSessionPhysicalPaymentRequested",
      // El cliente pidio un descuento sobre uno de sus productos (ej. jubilado) - se aprueba desde aqui.
      "tableSessionDiscountRequested",
      // El cliente se cambio de mesa (POS "Mover de mesa") - ver moveSessionToTable en el backend.
      "tableSessionMoved"
    ];

    const handleSessionEvent = (payload = {}, eventName = "") => {
      if (!payloadMatchesContext(payload)) return;

      if (eventName === "tableSessionMoved") {
        applyTableMoveLocally(payload.from_table?.id, payload.to_table?.id);
        return;
      }

      const session = normalizeTableSession(payload.session || payload.table_session || payload);
      const newRequest = eventName === "tableSessionRequested" && session.session_uuid && !notifiedSessionRequestsRef.current.has(session.session_uuid);

      if (["tableSessionRejected", "tableSessionClosed", "tableSessionExpired", "tableSessionCancelled"].includes(eventName)) {
        removeTableSession(session);
      } else if (session.session_uuid) {
        upsertTableSession(session);
      }

      if (newRequest) {
        notifiedSessionRequestsRef.current.add(session.session_uuid);
        AlertSmall("Nueva solicitud de mesa", "info");
        // Post-pay's request/approve handshake (pre-pay skips straight to
        // APPROVED/ACTIVE - see requestSession/consumePrepayToken on the backend) - this is
        // the very first signal staff gets that a customer wants a table, so it must ring here
        // exactly like it already does in PaymentPost.jsx's handleTableEvent, not just toast.
        startAlarm();
        if (session.table_id) markTableAlerted(session.table_id);
      }

      if (eventName === "tableSessionPaymentRequested") {
        const tableNumber = tablesRef.current.find((t) => t.id === session.table_id)?.number || session.table_name || session.table_id;
        startAlarm();
        markTableAlerted(session.table_id);
        AlertSmall(`Mesa ${tableNumber} · ${payload.participant_display_name || "El cliente"} solicitó pagar la cuenta`, "info");
      }

      if (eventName === "tableSessionDiscountRequested" && session.table_id) {
        const tableNumber = tablesRef.current.find((t) => t.id === session.table_id)?.number || session.table_name || session.table_id;
        startAlarm();
        markTableAlerted(session.table_id);
        Swal.fire({
          icon: "info",
          title: `Mesa ${tableNumber}`,
          text: `${payload.participant_display_name || "El cliente"} pidió descuento en ${payload.product_name || "un producto"}${payload.reason ? ` (${payload.reason})` : ""}`,
          showCancelButton: true,
          confirmButtonText: "Revisar",
          cancelButtonText: "Después"
        }).then((result) => {
          if (!result.isConfirmed) return;
          stopAlarm();
          clearTableAlert(session.table_id);
          setDiscountReviewTableId(session.table_id);
        });
      }

      // Pre-pay: the customer just paid on their own and the order already went straight to the
      // kitchen (see beginPrepayPayment on the backend) - staff never got a chance to see this
      // coming the way they would for a post-pay table, so this is the one moment that has to alert
      // them loudly. Guarded by payment_mode so a waiter authorizing a normal post-pay table from
      // the POS itself doesn't alarm on their own action.
      if (eventName === "tableSessionPaymentAuthorized" && String(payload.payment_mode).toUpperCase() === "PRE_PAY") {
        const tableNumber = tablesRef.current.find((t) => t.id === session.table_id)?.number || session.table_name || session.table_id;
        startAlarm();
        markTableAlerted(session.table_id);
        AlertSmall(`Mesa ${tableNumber} · El cliente ya pagó - el pedido se envió directo a cocina`, "success");
      }

      if (eventName === "tableSessionPhysicalPaymentRequested" && session.table_id) {
        const tableNumber = tablesRef.current.find((t) => t.id === session.table_id)?.number || session.table_name || session.table_id;
        upsertPosPaymentRequest(session.table_id, {
          session_uuid: session.session_uuid,
          payment_attempt_id: payload.payment_attempt_id,
          calculation_id: payload.calculation_id,
          amount: payload.amount,
          participant: payload.participant || null,
          items: Array.isArray(payload.items) ? payload.items : []
        });
        startAlarm();
        markTableAlerted(session.table_id);
        AlertSmall(`Mesa ${tableNumber} · ${payload.participant?.display_name || "El cliente"} pidió pagar $${Number(payload.amount || 0).toFixed(2)} en el POS`, "info");
      }

      if (!session.table_id) {
        fetchTableSessions();
        return;
      }

      // table_status viaja directo en el evento (approveSession/revokeSession/consumePrepayToken en
      // el backend, junto con el UPDATE real a la tabla `tables`) porque scheduleTableRefresh de abajo
      // nunca vuelve a pedir la fila de la mesa en si (solo la sesion y los pedidos) - sin esto la
      // mesa se queda con el estado "Disponible/Ocupada" que tenia al cargar la pagina, aunque el
      // backend ya haya actualizado el dato real, hasta un refresh completo.
      if (payload.table_status) {
        setTables((prev) => prev.map((t) => (
          String(t.id) === String(session.table_id)
            ? { ...t, status: payload.table_status, status_since: payload.table_status === "occupied" ? new Date().toISOString() : null }
            : t
        )));
      }

      // Fase 2/6/10: si el evento trae la orden completa (tableSessionOrderUpdated la incluye
      // siempre - ver comentario arriba), la aplicamos directo como snapshot y evitamos el GET.
      let appliedSnapshot = false;
      if (eventName === "tableSessionOrderUpdated" && payload.order) {
        appliedSnapshot = applyOrderSnapshotToTable(session.table_id, payload.order);
      }

      const closingEvents = ["tableSessionClosed", "tableSessionExpired", "tableSessionCancelled"];
      scheduleTableRefresh(session.table_id, {
        // El resumen de participantes/estado de sesion no viaja en el payload del pedido, asi que
        // siempre se refresca (via el mismo GET ya deduplicado/in-flight que abre la mesa).
        refreshSession: true,
        // Solo pedimos la orden completa por GET si no pudimos aplicar el snapshot del evento, o si
        // la sesion se cerro/expiro/cancelo (para limpiar table_orders/comensales locales).
        refreshOrders: !appliedSnapshot || closingEvents.includes(eventName)
      });
    };

    sessionEvents.forEach((eventName) => socket.on(eventName, (payload) => handleSessionEvent(payload, eventName)));

    // Cada accion del cliente e-commerce (ingreso, pedido, cambio, pedir pagar...) que el backend
    // guarda en el historial de la mesa - alimenta el modal "Actividad" sin tocar los Swal de arriba.
    const handleHistoryEvent = (payload = {}) => {
      if (!payloadMatchesContext(payload)) return;
      appendTableEvent(payload.table?.id || payload.event?.table_id, payload.event);
    };
    socket.on("tableSessionEventRecorded", handleHistoryEvent);
    socket.on("connect", fetchTableSessions);
    socket.on("reconnect", fetchTableSessions);

    return () => {
      const removeSocketListener = socket.off ? socket.off.bind(socket) : socket.removeListener?.bind(socket);
      sessionEvents.forEach((eventName) => removeSocketListener?.(eventName));
      removeSocketListener?.("tableSessionEventRecorded");
      removeSocketListener?.("connect", fetchTableSessions);
      removeSocketListener?.("reconnect", fetchTableSessions);
      socket.disconnect();

      // Fase 10: limpia timers de debounce pendientes para no llamar setState tras desmontar.
      refreshDebounceRef.current.forEach((pending) => {
        if (pending.timeoutId) clearTimeout(pending.timeoutId);
      });
      refreshDebounceRef.current.clear();
    };
  }, [branchoffice?.code, branchoffice?.id, business?.id, business?.code]);


  // Historial listo para pintar (hora ya formateada + si hay algo sin ver), por table_id. Aparte de
  // tablesWithTimer para no reformatear las horas en cada tick de 1s del timer.
  const historyByTable = useMemo(() => {
    const result = {};
    Object.entries(tableEventsByTable).forEach(([tableKey, events]) => {
      const list = events.map((ev) => ({ ...ev, time: formatHistoryTime(ev.occurred_at) }));
      const lastId = list.length ? list[list.length - 1].id : null;
      // Solicitudes de descuento sin resolver: cada solicitud abre la linea y su resolucion (aprobada,
      // rechazada o cancelada por quitar el producto) la cierra - mismo line_uuid.
      const pendingDiscountLines = new Set();
      list.forEach((ev) => {
        if (ev.type !== "discount" || !ev.line_uuid) return;
        if (ev.badge?.kind === "request") pendingDiscountLines.add(ev.line_uuid);
        else pendingDiscountLines.delete(ev.line_uuid);
      });
      result[tableKey] = {
        events: list,
        unseen: lastId !== null && seenEventIdByTable[tableKey] !== lastId,
        pendingDiscounts: pendingDiscountLines.size
      };
    });
    return result;
  }, [tableEventsByTable, seenEventIdByTable]);

  // elapsed_since (ms) en vez de elapsed_time (texto): el texto cambia cada segundo y obligaria a recalcular y
  // redibujar todas las mesas; el reloj vive en <ElapsedClock>. Esta lista ahora solo cambia con los datos.
  const tablesWithTimer = useMemo(() => {
    return tables.map((t) => {
      const tableSession = tableSessionsByTable[String(t.id)] || null;
      const hasPendingAlert = alertedTableIds.has(t.id);
      const posPaymentRequests = posPaymentRequestsByTable[String(t.id)] || [];
      const posPaymentRequest = posPaymentRequests[0] || null;
      const history = historyByTable[String(t.id)];
      const events = history?.events || [];
      const historyUnseen = Boolean(history?.unseen);
      const pendingDiscountCount = history?.pendingDiscounts || 0;
      if (t.status === "available") return { ...t, tableSession, elapsed_since: null, hasPendingAlert, posPaymentRequest, posPaymentRequests, events, historyUnseen, pendingDiscountCount };

      const since = t.status_since ? new Date(t.status_since).getTime() : null;
      if (!since || Number.isNaN(since)) return { ...t, tableSession, elapsed_since: null, hasPendingAlert, posPaymentRequest, posPaymentRequests, events, historyUnseen, pendingDiscountCount };

      return { ...t, tableSession, elapsed_since: since, hasPendingAlert, posPaymentRequest, posPaymentRequests, events, historyUnseen, pendingDiscountCount };
    });
  }, [tables, tableSessionsByTable, alertedTableIds, posPaymentRequestsByTable, historyByTable]);

  const normalizeTableSessionRequest = (raw = {}) => {
    const session = raw.session || raw.table_session || raw;
    const table = session.table || raw.table || {};
    const environment = session.environment || session.zone || table.environment || {};
    return {
      session_uuid: session.session_uuid || session.uuid || session.id || raw.session_uuid,
      table_id: session.table_id || table.id || raw.table_id,
      table_code: session.table_code || table.code || raw.table_code,
      table_name: session.table_name || table.name || raw.table_name,
      branchoffice_environment_id: session.branchoffice_environment_id || table.branchoffice_environment_id || table.environment_id || environment.id,
      zone_name: table.environment_name || environment.name || session.zone_name || raw.zone_name,
      status: session.status || raw.status,
      source: session.source || raw.source,
      requested_at: session.requested_at || raw.requested_at || session.createdAt || raw.createdAt,
      client_name: session.client_name || session.customer_name || session.customer?.name || session.client?.name || raw.client_name,
    };
  };


  //We fetch the pending table requests, this is called when the bell button is clicked so it is always up to date
  const fetchTableSessionRequests = async () => {
    try {
      const response = await TableSessionsFetchAPI.getPendingSessions(
        { branchoffice_code: branchoffice?.code, status: "PENDING", limit: 100 },
        token
      );
      const data = response?.data?.data;
      const source = Array.isArray(data) ? data : data?.items || response?.data?.items || [];
      const normalized = source.map(normalizeTableSessionRequest).filter((session) => session.session_uuid);
      setTableSessionRequests(normalized);
    } catch (error) {
      console.error("No se pudieron obtener las solicitudes de mesa", error);
    }
  };

  //Every time the requests bell is clicked we query the backend first so the count/list is always fresh, then we open the panel
  const handleOpenTableRequests = async () => {
    await fetchTableSessionRequests();
    setShowTableRequestsPanel(true);
  };

  // Hook to validate the session
  useEffect(() => {
    (async () => {
      try {
        await validSession()
      } catch (err) {
        console.log(err);
      }
    })();
  }, []);


  return (
    <>

      <div className="sp-app-x">
        <div className="sp-frame">
          <main className="sp-main">


            <RfmHeaderMain
              onOpenMenu={() => setDrawerOpen(true)}
              business_code={business?.code}
              branchoffice_code={branchoffice?.code}
              onToggleSidebar={() => { }}
              onOpenModal={openModal}
              tables={tablesWithTimer}
              updateTable={(id, patch) => {
                setTables(prev => prev.map(t => (t.id === id ? { ...t, ...patch } : t)));
                updateTable(id, patch);
              }}
              pendingTableRequestsCount={tableSessionRequests.length}
              onOpenTableRequests={handleOpenTableRequests}
              stats={{
                available: tables.filter(table => table.status === "available").length,
                occupied: tables.filter(table => table.status === "occupied").length,
                sales: tables.reduce((sum, t) => sum + Number(t.total || 0), 0)
              }} />


            <DailySummary
              collapsed={summaryCollapsed}
              onToggle={() => setSummaryCollapsed((c) => !c)}
              stats={{
                available: tables.filter(table => table.status === "available").length,
                occupied: tables.filter(table => table.status === "occupied").length,
                reserved: tables.filter(table => table.status === "reserved").length,
                sales: tables.reduce((sum, t) => sum + Number(t.total || 0), 0)
              }} />


            <RfmSidebar
              zones={zones}
              tables={tablesWithTimer}
              statuses={statuses}
              currentZone={currentZone}
              onSelectZone={selectZone}
              onOpenModal={openModal}
            />

            <RfmFloor
              zones={zones}
              tables={tablesWithTimer}
              objects={objects}
              statuses={statuses}
              currentZone={currentZone}
              activeZone={activeZone}
              zoomLevel={zoomLevel}
              editMode={editMode}
              onZoomChange={handleZoomChange}
              onToggleEditMode={toggleEditMode}
              onCellClick={handleCellClick}
              onCreateTableAt={handleCreateTableAt}

              onSelectTable={handleSelectTable}
              onEventsSeen={markTableEventsSeen}
              onChargePosPayment={handleChargePosPayment}
              onReviewDiscounts={(table) => setDiscountReviewTableId(table?.id ?? null)}
              onSelectObject={handleSelectObject}
              onMoveTable={handleMoveTable}
              onMoveObject={handleMoveObject}
              selectedTableId={selectedTableId}
              selectedObjectId={selectedObjectId}

              onOpenModal={openModal}

              onCreatePlantObject={handleCreatePlantObject}
              token={token}
              updateTable={updateTable}
              updateObject={updateObject}
            />
          </main>

        </div>
      </div>


      <RfmTableOffcanvas
        open={!!selectedTableId || !!selectedObjectId}
        table={tablesWithTimer.find(t => t.id === selectedTableId) || null}
        object={objects.find(o => o.id === selectedObjectId) || null}
        zones={zones}
        statuses={statuses}
        onClose={() => { setSelectedTableId(null); setSelectedObjectId(null); }}
        onUpdateTable={(id, patch) => {
          setTables(prev => prev.map(t => (t.id === id ? { ...t, ...patch } : t)));
          updateTable(id, patch);
        }}
        onDeleteTable={(id) => {
          deleteTable(id);
        }}
        onDeleteObject={(id) => deleteObject(id)}

        getReservationData={getReservationData}
        onOpenModal={onOpenModal}
        onCloseTableSession={handleCloseTableSession}
        onMoveTableSession={handleMoveTableSession}
        processingSessionId={processingSessionId}
        prepayEnabled={Number(branchoffice?.prepay_tables_enabled) === 1}
        onChargePosPayment={handleChargePosPayment}
      />

      <RfmTableRequestsPanel
        open={showTableRequestsPanel}
        onClose={() => setShowTableRequestsPanel(false)}
        requests={tableSessionRequests}
        zones={zones}
        processingSessionId={processingSessionId}
        onApprove={handleApproveTableSession}
        onReject={handleRejectTableSession}
      />


      {/* =========================
          MODALES (GLOBAL)
      ========================== */}

      <RfmModals
        modal={modal}
        onClose={() => setModal(null)}
        tables={tablesWithTimer}
        onCreateTempOrder={createTempOrderForTable}
        setTables={setTables}
        onOpenModal={onOpenModal}
        token={token}
        branchoffice_code={branchoffice?.code}
        business_code={business?.code}
        onOrdersChanged={refreshTableOrdersAndDiners}
        onRequireUserCodeLogin={() => {
          const machine = JSON.parse(localStorage.getItem("machine")) || {};
          if (Number(machine.user_code_validation) === 1) {
            setShowLoginByCode(true);
          }
        }}
        // "Cuenta pre-pago": el mesero decide cobrar productos aun pendientes en vez de forzar el
        // cierre - abre el mismo Centro de Pagos embebido que "Cobrar en terminal" (ver posCharge
        // arriba). El propio OrderDetailsModal arma el objeto completo (ya tiene `table` a mano).
        onOpenPosCharge={setPosCharge}
      />

      <TableDiscountRequestsModal
        open={discountReviewTableId !== null}
        onClose={() => setDiscountReviewTableId(null)}
        table={tablesWithTimer.find((t) => t.id === discountReviewTableId) || null}
        onResolved={(tableId) => refreshTableOrdersAndDiners(tableId)}
      />

      {/* "Cobrar en terminal": PaymentPost embebido y oculto; solo se ve su Centro de Pagos. */}
      {posCharge ? (
        <PaymentPost
          embedded
          posPaymentRequest={posCharge}
          business_code={business?.code}
          branchoffice_code={branchoffice?.code}
          onPosPaymentSettled={handlePosChargeSettled}
          onDismiss={() => setPosCharge(null)}
        />
      ) : null}


      <RfmCreateZoneModal
        open={activeModal === "create-zone"}
        onClose={closeModal}
        branchoffice={branchoffice}
        token={token}
        onCreate={({ name, color, grid_cols, grid_rows, id }) => {
          setZones((prev) => [...prev, { id, name, color, cols: grid_cols, rows: grid_rows }]);
          // opcional: cambiar a esa zona nueva
          setCurrentZone(id);
          setEditMode(false);
        }}
      />

      <RfmCreateTableModal
        open={activeModal === "create-table"}
        onClose={closeModal}
        zones={zones}
        onCreate={(payload) => {
          const zone = payload.zone || zones[0]?.id;
          //We create the table at position 0,0 (it will be moved later if needed)
          handleCreateTableAt(0, 0, payload);
          setCurrentZone(Number(zone));
          setEditMode(false);
        }}
      />


      <RfmReservationModal
        open={activeModal === "reservation"}
        onClose={closeModal}
        tables={tablesWithTimer}
        onCreate={async ({ name, phone, email, guests, date, time, note, status, tableId }) => {
          //We create the reservation, then we update the table with the reservation id and status reserved, if tableId is provided we use that table, if not we use the first available table, if no table is available we do nothing
          let reservation = await createClientReservation({
            business_code: business?.code,
            branchoffice_code: branchoffice?.code,
            guest_name: name,
            guest_phone: phone,
            guest_email: email,
            reserved_at: date + " " + time,
            people_qty: guests,
            notes: note,
            status,
          })
          //We select the table by id, if not found we select the first available, if not found we do nothing
          setTables((prev) => {
            let picked = null;

            if (tableId) picked = prev.find((t) => String(t.id) === String(tableId));
            if (!picked) picked = prev.find((t) => t.status === "available");
            if (!picked) return prev;

            //We update the table and we add the rservation id data to the table 
            if (picked.id) {
              updateTable(picked.id, { reservation_id: reservation.data.reservation.reservation_id, status: "reserved" });
            }
            return prev.map((t) =>
              t.id === picked.id
                ? {
                  ...t,
                  status: "reserved",
                  time: time || t.time,
                  reservation: { name, phone, guests, date, time, email },
                }
                : t
            );
          });
        }}
      />

      <RfmMergeTablesModal
        open={activeModal === "merge-tables"}
        onClose={closeModal}
        zones={zones}
        tables={tablesWithTimer}
        onMerge={({ tableIds }) => {
          setTables((prev) => {
            const [mainId, ...others] = tableIds;
            const main = prev.find((t) => t.id === mainId);
            if (!main) return prev;

            const newChairs = tableIds.reduce((sum, id) => {
              const tt = prev.find((x) => x.id === id);
              return sum + (tt?.chairs || 0);
            }, 0);

            return prev.map((t) => {
              if (t.id === mainId) return { ...t, mergedWith: others, chairs: newChairs };
              if (others.includes(t.id)) return { ...t, mergedWith: [mainId] };
              return t;
            });
          });
        }}
      />

      <RfmManageStatusModal
        open={activeModal === "manage-status"}
        onClose={closeModal}
        statuses={statuses}
        onAdd={({ name, color }) => {
          const id = safeSlug(name);
          if (!id) return;
          setStatuses((prev) => [...prev, { id, name, color, icon: "bi bi-circle-fill" }]);
        }}
        onRemove={(id) => setStatuses((prev) => prev.filter((s) => s.id !== id))}
      />

      {/** =============== Zones Modals ====================*/}
      <RfmCreateObjectModal
        open={activeModal === "create-object"}
        onClose={closeModal}
        zones={zones}
        defaultZoneId={currentZone !== "all" ? currentZone : zones?.[0]?.id}
        onCreate={(payload) => {
          const zone = payload.zone || zones[0]?.id;
          //We create the object at position 0,0 (it will be moved later if needed)
          createObject(payload);
          setCurrentZone(Number(zone));
          setEditMode(true);
        }}
      />

      <RfmEditZoneModal
        open={activeModal === "edit-zone"}
        onClose={closeModal}
        zone={zones.find((z) => z.id === currentZone) || null}
        onSave={(payload) => {
          updateBranchOfficeEnvironment(payload.id, {
            name: payload.name,
            color: payload.color,
            grid_cols: payload.grid_cols,
            grid_rows: payload.grid_rows,
          });

          setZones((prev) =>
            prev.map((z) =>
              z.id === payload.id
                ? {
                  ...z,
                  name: payload.name,
                  color: payload.color,
                  cols: payload.grid_cols,
                  rows: payload.grid_rows,
                }
                : z
            )
          );
          closeModal();
        }}
      />
      <RfmDeleteZoneModal
        open={activeModal === "delete-zone"}
        onClose={closeModal}
        zone={zones.find((z) => z.id === currentZone) || null}
        onDelete={async (id) => {
          let deleted = await deleteBranchOfficeEnvironment(id);
          if (deleted) {
            //We also delete the zone from the state, and we set the current zone to "all"
            setZones((prev) => prev.filter((z) => z.id !== id));
            setCurrentZone("all");
            closeModal();
          }

        }}
      />

      {/* =============== Comensales / Orders Modals ====================*/}
      <RfmAddComensalModal
        open={modal?.name === "add-comensal"}
        onClose={() => setModal(null)}
        orderOptions={modal?.payload?.orderOptions || []}
        preselectedOrderId={modal?.payload?.preselectedOrderId || ""}
        onAdd={async ({ name, seat_number, order_id }) => {
          try {
            const tableId = modal?.payload?.tableId;

            if (!tableId || !order_id) {
              return ErrorAlert("No se encontró la orden seleccionada");
            }

            const resp = await TablesFetchAPI.addTableDinners(
              tableId,
              {
                order_id,
                name,
                seat_number,
              },
              token
            );

            if (!resp?.data?.success) {
              throw new Error(resp?.data?.message || "No se pudo agregar el comensal");
            }

            const dinersResp = await TablesFetchAPI.getTableDinners(tableId, token);
            const normalizedDiners = normalizeBackendDiners(dinersResp?.data?.diners || []).map((d) =>
              d.diner_id === resp?.data?.diner?.diner_id
                ? { ...d, selected_order_id: order_id }
                : d
            );

            setTables((prev) =>
              prev.map((t) =>
                t.id !== tableId
                  ? t
                  : {
                    ...t,
                    comensales: normalizedDiners,
                    guests: normalizedDiners.length,
                  }
              )
            );

            setModal(null);
          } catch (err) {
            ErrorAlert("Error al agregar comensal", err.message || "Ocurrió un error");
          }
        }}
      />

      <RfmAddOrderToComensalModal
        open={modal?.name === "add-order-comensal"}
        onClose={() => setModal(null)}
        dinerName={modal?.payload?.dinerName || ""}
        orderOptions={modal?.payload?.orderOptions || []}
        preselectedOrderId={modal?.payload?.orderId || ""}
        getAvailableItemsByOrder={(orderId) => {
          const table = tables.find((t) => t.id === modal?.payload?.tableId);
          const order = (table?.table_orders || []).find((o) => String(o.id) === String(orderId));
          return getUnassignedItemsFromOrder(order);
        }}
        onAdd={async ({ items, order_id }) => {
          try {
            const tableId = modal?.payload?.tableId;
            const dinerId = modal?.payload?.dinerId;

            if (!tableId || !order_id || !dinerId || !items?.length) {
              return ErrorAlert("Datos incompletos para asignar productos");
            }

            const resp = await TablesFetchAPI.assignItemsToDiner(
              tableId,
              {
                order_id,
                diner_id: dinerId,
                items: items.map((item) => ({
                  source: item.source,
                  index: item.index,
                })),
              },
              token
            );

            if (!resp?.data?.success) {
              throw new Error(resp?.data?.message || "No se pudieron asignar los productos");
            }

            const [ordersResp, dinersResp] = await Promise.all([
              TablesFetchAPI.getTempOrdersByTableId(tableId, token),
              TablesFetchAPI.getTableDinners(tableId, token),
            ]);

            const tableOrders = normalizeBackendOrdersToTableOrders(ordersResp?.data?.orders || []);
            const normalizedDiners = normalizeBackendDiners(dinersResp?.data?.diners || []).map((d) =>
              d.diner_id === dinerId
                ? { ...d, selected_order_id: order_id }
                : d
            );

            const totalToPay = tableOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);

            setTables((prev) =>
              prev.map((t) =>
                t.id !== tableId
                  ? t
                  : {
                    ...t,
                    total: Number(totalToPay),
                    table_orders: tableOrders,
                    backend_orders: tableOrders,
                    comensales: normalizedDiners,
                    guests: normalizedDiners.length,
                  }
              )
            );

            setModal(null);
          } catch (err) {
            ErrorAlert("Error al asignar productos", err.message || "Ocurrió un error");
          }
        }}
      />

      <RfmCloseTableSessionModal
        open={modal?.name === "close-table-session"}
        onClose={() => setModal(null)}
        hasOpenOrder={Boolean(modal?.payload?.hasOpenOrder)}
        processing={Boolean(processingSessionId)}
        onConfirm={confirmCloseTableSession}
      />

      <RfmMoveTableSessionModal
        open={modal?.name === "move-table-session"}
        onClose={() => setModal(null)}
        fromTable={modal?.payload?.table}
        candidates={getMoveTargetCandidates()}
        processing={Boolean(processingSessionId)}
        onConfirm={confirmMoveTableSession}
      />

      <RfmSplitBillModal
        open={activeModal === "split-bill"}
        onClose={closeModal}
        remaining={remainingForSplit}
        onApply={({ count }) => {
          console.log("split bill count:", count);
        }}
      />


      <ModalLoginUserByCode
        showLoginByCode={showLoginByCode}
        setShowLoginByCode={setShowLoginByCode}
        businessLogo={localStorage.getItem("businessLogo") || ""}
        parthners={[]}
        selectRestaurant={() => { }}
        setResetData={() => { }}
        calculateInvoiceTotals={() => { }}
        shoppingCart={[]}
        shoppingCartCombos={[]}
        setShoppingCart={() => { }}
        setShoppingCartCombos={() => { }}
        setShowModalWarehouses={() => { }}
        warehouses={[]}
      />

      {showAudioUnlock && (
        <div className="position-fixed bottom-0 start-50 translate-middle-x mb-2 px-3 py-2 rounded-3 bg-warning text-dark shadow d-none"
          style={{ zIndex: 2000 }}>
          <button className="btn btn-sm btn-dark me-2" onClick={handleUnlockAudio} title="Habilitar sonido">
            🔊 Habilitar sonido
          </button>
          <small>Toque “Habilitar sonido” una vez para permitir la alerta audible.</small>
        </div>
      )}
    </>
  );
}
